import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts';
import { Presentation, FileDown, ChevronLeft, ChevronRight, X, Settings2, Loader2, Check } from 'lucide-react';
import clsx from 'clsx';
import type { Flow, Card, Colaborador } from '../../types';
import { calcularHorasSLA } from '../../utils/slaUtils';
import { formatShortCode } from '../../utils/shortCodeUtils';
import {
  calcular, detectarDimensoes, tiposAutomaticos, camposValor, intervaloDe, PERIODOS, nomeLimpo,
  type TipoEtapa, type PeriodoId, type CelulaSla, type ResultadoApres, type LinhaDim, type CardDef, type FluxoDef, type Dimensao,
} from './apresentacaoCalc';

// Apresentação de fluxos (Dashboard → Apresentação, 05/11/2026, pedido do André): escolhe o fluxo,
// o período e (se quiser) filial/centro de custo, e a tela monta os slides sozinha a partir dos cards,
// etapas, campos e histórico do fluxo. Tela cheia com ←/→ (passador de slides manda PageUp/PageDown)
// e PDF deitado (16:9). Só leitura: os números vêm de apresentacaoCalc.ts.

interface Props {
  flow: Flow;
  cards: Card[];               // resumo já carregado pelo App (usado se a busca própria falhar)
  colaboradores: Colaborador[];
}

const W = 1280, H = 720;
// slides que só entram se a pessoa ligar no Ajustar (05/11/2026, André: "Por responsável do card" não precisa)
const DESLIGADOS_PADRAO = ['dim___responsavel'];
const AZUL = '#2a78d6', VERDE = '#1baf7a', VERMELHO = '#e34948', CINZA = '#cbd5e1';
const TINTA = '#0f172a', TINTA2 = '#475569', TINTA3 = '#64748b';

// ---------- formatação ----------
const int = (n: number) => Math.round(n).toLocaleString('pt-BR');
const moeda = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const moedaCurta = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e6) return `R$ ${(n / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`;
  if (a >= 1e4) return `R$ ${(n / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`;
  return moeda(n);
};
const dias = (d: number | null) => d === null ? '—' : d < 2 ? `${(d * 24).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h` : `${d.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`;
const horasTxt = (h: number | null) => h === null ? '—' : dias(h / 24);
const pct = (a: number, b: number) => b ? `${Math.round((a / b) * 100)}%` : '—';
const corta = (s: string, n: number) => s.length > n ? s.slice(0, n - 1) + '…' : s;

function sessao() {
  try {
    const s = JSON.parse(localStorage.getItem('session') || 'null');
    return { empresaId: s?.empresa?.id || s?.usuario?.empresaId || '', email: s?.usuario?.email || '', empresaNome: s?.empresa?.nome || '' };
  } catch { return { empresaId: '', email: '', empresaNome: '' }; }
}
const lerPref = (k: string) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
const gravarPref = (k: string, v: any) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* sem storage */ } };

// ---------- blocos visuais dos slides ----------

const Slide: React.FC<{ titulo?: string; sub?: string; rodape: string; n: number; total: number; children: React.ReactNode }> = ({ titulo, sub, rodape, n, total, children }) => (
  <div style={{ width: W, height: H, background: '#fff', color: TINTA, position: 'relative', overflow: 'hidden', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
    <div style={{ position: 'absolute', left: 0, top: 0, right: 0, height: 8, background: `linear-gradient(90deg, ${AZUL}, #6250d6)` }} />
    {titulo && (
      <div style={{ position: 'absolute', left: 64, top: 44, right: 64 }}>
        <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.8, lineHeight: 1.1 }}>{titulo}</div>
        {sub && <div style={{ fontSize: 19, color: TINTA2, marginTop: 8 }}>{sub}</div>}
      </div>
    )}
    <div style={{ position: 'absolute', left: 64, right: 64, top: titulo ? (sub ? 150 : 124) : 0, bottom: 64 }}>{children}</div>
    <div style={{ position: 'absolute', left: 64, right: 64, bottom: 24, display: 'flex', justifyContent: 'space-between', fontSize: 14, color: TINTA3 }}>
      <span>{rodape}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{n} / {total}</span>
    </div>
  </div>
);

const Kpi: React.FC<{ rotulo: string; valor: string; sub?: string; destaque?: string }> = ({ rotulo, valor, sub, destaque }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: 18, padding: '22px 26px', background: '#f8fafc', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
    <div style={{ fontSize: 17, color: TINTA2, fontWeight: 600 }}>{rotulo}</div>
    <div style={{ fontSize: valor.length > 9 ? 40 : 50, fontWeight: 800, whiteSpace: 'nowrap', letterSpacing: -1.5, lineHeight: 1.1, marginTop: 6, color: destaque || TINTA, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
    {sub && <div style={{ fontSize: 15, color: TINTA3, marginTop: 6 }}>{sub}</div>}
  </div>
);

/** Barras horizontais em HTML: rótulo, barra proporcional, número à direita. */
const Barras: React.FC<{ linhas: { rotulo: string; valor: number; txt: string; cor?: string; extra?: string }[]; altura: number; larguraRotulo?: number }> = ({ linhas, altura, larguraRotulo = 300 }) => {
  const max = Math.max(1, ...linhas.map(l => l.valor));
  const h = Math.min(46, Math.floor(altura / Math.max(1, linhas.length)));
  return (
    <div>
      {linhas.map((l, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', height: h, gap: 14 }}>
          <div style={{ width: larguraRotulo, fontSize: Math.min(18, h * 0.5), color: TINTA, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={l.rotulo}>{l.rotulo}</div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: `${Math.max(0.4, (l.valor / max) * 100)}%`, height: Math.max(10, h * 0.56), background: l.cor || AZUL, borderRadius: '0 4px 4px 0' }} />
            <span style={{ fontSize: Math.min(18, h * 0.5), fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{l.txt}</span>
            {l.extra && <span style={{ fontSize: Math.min(15, h * 0.42), color: TINTA3, whiteSpace: 'nowrap' }}>{l.extra}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

const Tabela: React.FC<{ cab: string[]; linhas: (string | number)[][]; alinhar?: ('l' | 'r')[]; fonte?: number }> = ({ cab, linhas, alinhar = [], fonte = 16 }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fonte, fontVariantNumeric: 'tabular-nums' }}>
    <thead>
      <tr>{cab.map((c, i) => <th key={i} style={{ textAlign: alinhar[i] === 'r' ? 'right' : 'left', color: TINTA3, fontWeight: 600, padding: '6px 8px', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{c}</th>)}</tr>
    </thead>
    <tbody>
      {linhas.map((l, i) => (
        <tr key={i} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
          {l.map((v, j) => <td key={j} style={{ textAlign: alinhar[j] === 'r' ? 'right' : 'left', padding: '7px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: j === 0 ? 420 : 260 }}>{v}</td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

const Legenda: React.FC<{ itens: [string, string][] }> = ({ itens }) => (
  <div style={{ display: 'flex', gap: 22, fontSize: 16, color: TINTA2 }}>
    {itens.map(([cor, nome]) => <span key={nome} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ width: 14, height: 14, borderRadius: 3, background: cor }} />{nome}</span>)}
  </div>
);

/** Desenha um slide 1280×720 escalado para a largura disponível. */
const Moldura: React.FC<{ children: React.ReactNode; largura?: number; altura?: number }> = ({ children, largura, altura }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [esc, setEsc] = useState(0.5);
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const calc = () => {
      const wDisp = largura ?? el.clientWidth;
      const hDisp = altura ?? Infinity;
      setEsc(Math.min(wDisp / W, hDisp / H));
    };
    calc();
    const ro = new ResizeObserver(calc); ro.observe(el);
    return () => ro.disconnect();
  }, [largura, altura]);
  return (
    <div ref={ref} style={{ width: largura ?? '100%', height: H * esc, position: 'relative' }}>
      <div style={{ width: W, height: H, transform: `scale(${esc})`, transformOrigin: 'top left', position: 'absolute', left: 0, top: 0 }}>{children}</div>
    </div>
  );
};

// ---------- montagem dos slides ----------

interface DefSlide { id: string; nome: string; render: (n: number, total: number) => React.ReactNode }

const NOME_PAPEL: Record<string, string> = {
  filial: 'Por filial', centro: 'Por centro de custo', solicitante: 'Quem mais solicita', fornecedor: 'Principais fornecedores',
  responsavel: 'Por responsável do card', categoria: 'Por',
};

function montarSlides(r: ResultadoApres, ctx: { empresa: string; filtrosTxt: string; geradoEm: Date; temValor: boolean }): DefSlide[] {
  const rodape = `${r.nomeFluxo} · ${r.periodoTexto}${ctx.filtrosTxt ? ' · ' + ctx.filtrosTxt : ''}`;
  const S: DefSlide[] = [];
  const sl = (id: string, nome: string, titulo: string, sub: string | undefined, corpo: () => React.ReactNode) =>
    S.push({ id, nome, render: (n, t) => <Slide titulo={titulo} sub={sub} rodape={rodape} n={n} total={t}>{corpo()}</Slide> });

  // 1. capa
  S.push({
    id: 'capa', nome: 'Capa', render: (n, t) => (
      <div style={{ width: W, height: H, position: 'relative', overflow: 'hidden', color: '#fff', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
        background: 'radial-gradient(1200px 600px at 85% 10%, #3b5bdb 0%, transparent 60%), linear-gradient(135deg, #0b1220 0%, #13204a 55%, #1d3a8a 100%)' }}>
        <div style={{ position: 'absolute', right: -120, bottom: -160, width: 560, height: 560, borderRadius: '50%', border: '60px solid rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', left: 80, top: 72, fontSize: 20, letterSpacing: 4, textTransform: 'uppercase', opacity: 0.75 }}>{ctx.empresa || 'Escalone'} · Apresentação de fluxo</div>
        <div style={{ position: 'absolute', left: 80, right: 120, top: 220 }}>
          <div style={{ fontSize: 72, fontWeight: 800, letterSpacing: -2, lineHeight: 1.05 }}>{r.nomeFluxo}</div>
          <div style={{ fontSize: 28, marginTop: 24, opacity: 0.9 }}>{r.periodoTexto.charAt(0).toUpperCase() + r.periodoTexto.slice(1)}</div>
          {ctx.filtrosTxt && <div style={{ fontSize: 22, marginTop: 10, opacity: 0.75 }}>{ctx.filtrosTxt}</div>}
        </div>
        <div style={{ position: 'absolute', left: 80, bottom: 64, display: 'flex', gap: 56 }}>
          {[[int(r.entradas), 'entradas'], [int(r.concluidos), 'concluídos'], [int(r.abertosHoje), 'em aberto hoje']].map(([v, l]) => (
            <div key={l}><div style={{ fontSize: 44, fontWeight: 800 }}>{v}</div><div style={{ fontSize: 18, opacity: 0.7 }}>{l}</div></div>
          ))}
        </div>
        <div style={{ position: 'absolute', right: 80, bottom: 70, fontSize: 16, opacity: 0.6, textAlign: 'right' }}>
          Gerado no Escalone em {ctx.geradoEm.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}<br />{n} / {t}
        </div>
      </div>
    ),
  });

  // 2. números-chave
  sl('kpis', 'Números-chave', 'Números-chave', undefined, () => {
    const k: React.ReactNode[] = [
      <Kpi key="e" rotulo="Entradas no período" valor={int(r.entradas)} sub="cards criados" />,
      <Kpi key="c" rotulo="Concluídos no período" valor={int(r.concluidos)} sub="pela data de conclusão" destaque={VERDE} />,
      <Kpi key="a" rotulo="Em aberto hoje" valor={int(r.abertosHoje)} sub="em etapas de andamento" />,
      <Kpi key="t" rotulo="Fora do SLA hoje" valor={int(r.atrasadosHoje)} sub={r.comSlaHoje ? `${pct(r.atrasadosHoje, r.comSlaHoje)} dos ${int(r.comSlaHoje)} em etapa com SLA` : 'nenhuma etapa com SLA'} destaque={r.atrasadosHoje ? VERMELHO : undefined} />,
      <Kpi key="m" rotulo="Tempo médio de ciclo" valor={dias(r.cicloMedioDias)} sub={`da criação à conclusão · mediana ${dias(r.cicloMedianoDias)}`} />,
      <Kpi key="r" rotulo="Reprovados no período" valor={int(r.reprovados)} sub={r.entradas ? `${pct(r.reprovados, r.entradas)} das entradas` : undefined} />,
    ];
    if (ctx.temValor) {
      k.push(<Kpi key="v" rotulo="Valor das entradas" valor={moedaCurta(r.valorEntradas)} sub={`${int(r.cardsComValor)} cards com valor · sem reprovados`} />);
      k.push(<Kpi key="tm" rotulo="Valor médio por card" valor={r.ticketMedio !== null ? moedaCurta(r.ticketMedio) : '—'} sub={`concluído no período: ${moedaCurta(r.valorConcluido)}`} />);
    }
    return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${k.length > 6 ? 4 : 3}, 1fr)`, gridAutoRows: '1fr', gap: 20, height: '100%' }}>{k}</div>;
  });

  // 3. evolução
  if (r.evolucao.length >= 2 && (r.entradas || r.concluidos)) {
    const g = r.granularidade === 'mes' ? 'mês a mês' : 'semana a semana';
    sl('evolucao', 'Evolução', `Evolução ${g}`, 'Entradas (criados) e concluídos em cada ' + (r.granularidade === 'mes' ? 'mês' : 'semana'), () => {
      const comValor = ctx.temValor && r.evolucao.some(b => b.valor > 0);
      const h1 = comValor ? 290 : 470;
      const fs = r.evolucao.length > 12 ? 13 : 16;
      return (
        <div>
          <Legenda itens={[[AZUL, 'Entradas'], [VERDE, 'Concluídos']]} />
          <BarChart width={W - 128} height={h1} data={r.evolucao} margin={{ top: 26, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap="22%">
            <CartesianGrid vertical={false} stroke="#eef2f7" />
            <XAxis dataKey="rotulo" tick={{ fontSize: fs, fill: TINTA2 }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 14, fill: TINTA3 }} axisLine={false} tickLine={false} width={44} />
            <Bar dataKey="entradas" fill={AZUL} radius={[4, 4, 0, 0]} isAnimationActive={false}><LabelList dataKey="entradas" position="top" style={{ fontSize: fs - 1, fill: TINTA, fontWeight: 600 }} /></Bar>
            <Bar dataKey="concluidos" fill={VERDE} radius={[4, 4, 0, 0]} isAnimationActive={false}><LabelList dataKey="concluidos" position="top" style={{ fontSize: fs - 1, fill: TINTA, fontWeight: 600 }} /></Bar>
          </BarChart>
          {comValor && (<>
            <div style={{ fontSize: 17, fontWeight: 700, color: TINTA2, marginTop: 4 }}>Valor das entradas ({r.valorNome})</div>
            <BarChart width={W - 128} height={170} data={r.evolucao} margin={{ top: 24, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
              <XAxis dataKey="rotulo" tick={{ fontSize: fs, fill: TINTA2 }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
              <YAxis hide />
              <Bar dataKey="valor" fill="#6250d6" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                <LabelList dataKey="valor" position="top" formatter={(v: any) => v ? moedaCurta(Number(v)).replace('R$ ', '') : ''} style={{ fontSize: fs - 2, fill: TINTA, fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </>)}
        </div>
      );
    });
  }

  // 4. etapas (funil / gargalos)
  const etapasAnd = r.etapas.filter(e => e.tipo === 'andamento' && e.passaram > 0);
  if (etapasAnd.length) {
    const top3 = new Set([...etapasAnd].filter(e => e.tempoMedioH !== null && e.visitas >= 10).sort((a, b) => (b.tempoMedioH ?? 0) - (a.tempoMedioH ?? 0)).slice(0, 3).map(e => e.id));
    const partes: typeof etapasAnd[] = etapasAnd.length > 13 ? [etapasAnd.slice(0, Math.ceil(etapasAnd.length / 2)), etapasAnd.slice(Math.ceil(etapasAnd.length / 2))] : [etapasAnd];
    const maxT = Math.max(1, ...etapasAnd.map(e => e.tempoMedioH ?? 0));
    partes.forEach((parte, pi) => sl(`etapas${pi}`, partes.length > 1 ? `Tempo por etapa (${pi + 1}/${partes.length})` : 'Tempo por etapa',
      'Tempo médio em cada etapa' + (partes.length > 1 ? ` (${pi + 1}/${partes.length})` : ''),
      'Passagens que começaram no período · em vermelho, os 3 maiores tempos médios (etapas com 10 passagens ou mais)', () => (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '330px 1fr 120px 120px 120px', gap: 12, fontSize: 15, color: TINTA3, fontWeight: 600, padding: '0 0 8px', borderBottom: '2px solid #e2e8f0' }}>
            <span style={{ textAlign: 'right' }}>Etapa</span><span>Tempo médio (mediana)</span><span style={{ textAlign: 'right' }}>Passaram</span><span style={{ textAlign: 'right' }}>Paradas hoje</span><span style={{ textAlign: 'right' }}>Fora do SLA</span>
          </div>
          {parte.map(e => {
            const h = Math.min(44, Math.floor(440 / parte.length));
            const gargalo = top3.has(e.id);
            return (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '330px 1fr 120px 120px 120px', gap: 12, alignItems: 'center', height: h, fontSize: Math.min(18, h * 0.46), borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: gargalo ? 700 : 400 }}>{e.nome}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: `${Math.max(0.5, ((e.tempoMedioH ?? 0) / maxT) * 70)}%`, height: Math.max(8, h * 0.45), background: gargalo ? VERMELHO : AZUL, borderRadius: '0 4px 4px 0' }} />
                  <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{horasTxt(e.tempoMedioH)}</span>
                  <span style={{ color: TINTA3, whiteSpace: 'nowrap', fontSize: '0.85em' }}>({horasTxt(e.tempoMedianoH)}{e.slaH ? ` · SLA ${e.slaH} h` : ''})</span>
                </span>
                <span style={{ textAlign: 'right' }}>{int(e.passaram)}</span>
                <span style={{ textAlign: 'right' }}>{e.abertosAgora ? int(e.abertosAgora) : '–'}</span>
                <span style={{ textAlign: 'right', color: e.atrasadosAgora ? VERMELHO : TINTA3, fontWeight: e.atrasadosAgora ? 700 : 400 }}>{e.atrasadosAgora ? int(e.atrasadosAgora) : '–'}</span>
              </div>
            );
          })}
        </div>
      )));
  }

  // 4b. evolução do SLA por etapa (05/11/2026, André: "quebrar por 3 ciclos, cada mês, pra ver a evolução do SLA")
  if (r.slaJanelas.length >= 2 && r.slaMensal.length) slidesSlaMensal(sl, r);

  // 5. em aberto hoje: onde está parado e há quanto tempo
  if (r.abertosHoje > 0) {
    sl('aberto', 'Em aberto hoje', 'Em aberto hoje', `${int(r.abertosHoje)} cards em andamento · ${int(r.atrasadosHoje)} fora do SLA da etapa`, () => {
      const porEtapa = r.etapas.filter(e => e.abertosAgora > 0).sort((a, b) => b.abertosAgora - a.abertosAgora).slice(0, 10);
      const maxE = Math.max(1, ...porEtapa.map(e => e.abertosAgora));
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 48, height: '100%' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>Por etapa atual</div>
              <Legenda itens={[[CINZA, 'No prazo / sem SLA'], [VERMELHO, 'Fora do SLA']]} />
            </div>
            {porEtapa.map(e => {
              const h = Math.min(44, Math.floor(430 / porEtapa.length));
              return (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, height: h }}>
                  <span style={{ width: 230, textAlign: 'right', fontSize: Math.min(17, h * 0.45), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.nome}</span>
                  <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'flex', width: `${(e.abertosAgora / maxE) * 80}%`, height: Math.max(10, h * 0.5), gap: 2 }}>
                      {e.abertosAgora - e.atrasadosAgora > 0 && <span style={{ flex: e.abertosAgora - e.atrasadosAgora, background: CINZA, borderRadius: e.atrasadosAgora ? 0 : '0 4px 4px 0' }} />}
                      {e.atrasadosAgora > 0 && <span style={{ flex: e.atrasadosAgora, background: VERMELHO, borderRadius: '0 4px 4px 0' }} />}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: Math.min(17, h * 0.45), whiteSpace: 'nowrap' }}>{e.abertosAgora}{e.atrasadosAgora ? <span style={{ color: VERMELHO }}> · {e.atrasadosAgora} fora</span> : null}</span>
                  </span>
                </div>
              );
            })}
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Idade desde a criação</div>
            <BarChart width={480} height={420} data={r.aging} margin={{ top: 30, right: 4, left: 4, bottom: 0 }} barCategoryGap="18%">
              <XAxis dataKey="faixa" tick={{ fontSize: 14, fill: TINTA2 }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} interval={0} />
              <YAxis hide allowDecimals={false} />
              <Bar dataKey="qtd" fill={AZUL} radius={[4, 4, 0, 0]} isAnimationActive={false}><LabelList dataKey="qtd" position="top" style={{ fontSize: 18, fill: TINTA, fontWeight: 700 }} /></Bar>
            </BarChart>
          </div>
        </div>
      );
    });
  }

  // 6. dimensões
  r.dims.forEach(({ dim, linhas }) => slideDimensao(sl, dim, linhas, r, ctx.temValor));

  // 7. destaques
  if (r.maioresValores.length || r.maisAntigos.length) {
    sl('destaques', 'Destaques', 'Destaques', undefined, () => {
      const cod = (c: { id: string; shortCode?: string }) => c.shortCode ? `#${c.shortCode}` : (c.id ? formatShortCode(c.id) : '');
      const colunas = r.maioresValores.length && r.maisAntigos.length;
      return (
        <div style={{ display: 'grid', gridTemplateColumns: colunas ? '1fr 1fr' : '1fr', gap: 40 }}>
          {r.maioresValores.length > 0 && (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Maiores valores do período</div>
              <Tabela fonte={15} cab={['Card', 'Etapa', 'Valor']} alinhar={['l', 'l', 'r']}
                linhas={r.maioresValores.map(c => [`${cod(c)} ${corta(c.titulo, colunas ? 30 : 60)}`, corta(c.etapa, 18), moeda(c.valor)])} />
            </div>
          )}
          {r.maisAntigos.length > 0 && (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Em aberto há mais tempo</div>
              <Tabela fonte={15} cab={['Card', 'Etapa atual', 'Dias']} alinhar={['l', 'l', 'r']}
                linhas={r.maisAntigos.map(c => [`${cod(c)} ${corta(c.titulo, colunas ? 30 : 60)}`, corta(c.etapa, 20), int(c.dias)])} />
            </div>
          )}
        </div>
      );
    });
  }

  // 8. encerramento + definições
  sl('fim', 'Encerramento e definições', 'Como os números foram calculados', undefined, () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 48 }}>
      <ul style={{ fontSize: 17, color: TINTA2, lineHeight: 1.5, paddingLeft: 20, listStyle: 'disc' }}>
        <li><b style={{ color: TINTA }}>Entradas:</b> cards criados no período.</li>
        <li><b style={{ color: TINTA }}>Concluídos:</b> cards que hoje estão em etapa de conclusão ({r.etapas.filter(e => e.tipo === 'concluida').map(e => e.nome).join(', ') || '—'}), contados pela data em que chegaram lá.</li>
        <li><b style={{ color: TINTA }}>Reprovados:</b> cards hoje em {r.etapas.filter(e => e.tipo === 'negativa').map(e => e.nome).join(', ') || 'etapa de reprovação (nenhuma marcada)'}.</li>
        <li><b style={{ color: TINTA }}>Em aberto / fora do SLA:</b> situação de hoje; fora do SLA = tempo na etapa atual maior que o prazo configurado nela (dias úteis quando a etapa ignora fim de semana e feriado).</li>
        {r.slaJanelas.length >= 2 && <li><b style={{ color: TINTA }}>Evolução do SLA:</b> passagens agrupadas pelo {r.slaCiclos ? 'ciclo de 30 dias' : 'mês'} em que entraram na etapa; % no SLA = passagens que saíram dentro do prazo da etapa; tendência compara o 1º e o último {r.slaCiclos ? 'ciclo' : 'mês'} mostrados (neutra abaixo de 10%); "no prazo" do fluxo = concluídos sem nenhuma passagem fora do SLA.</li>}
        <li><b style={{ color: TINTA }}>Ciclo:</b> da criação à conclusão, em dias corridos. Tempo por etapa: horas corridas de cada passagem; a que ainda está aberta conta até agora.</li>
        {ctx.temValor && <li><b style={{ color: TINTA }}>Valor:</b> campo {r.valorNome}; card com mais de uma filial/centro de custo tem o valor dividido igual entre eles. Reprovados ficam fora.</li>}
        {r.semDataConclusao > 0 && <li>{r.semDataConclusao} card(s) concluído(s) sem data no histórico ficaram fora da conta de concluídos.</li>}
      </ul>
      <div style={{ background: 'linear-gradient(135deg, #13204a, #1d3a8a)', color: '#fff', borderRadius: 24, padding: 36, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15 }}>{r.nomeFluxo}</div>
        <div style={{ fontSize: 18, opacity: 0.8, marginTop: 12 }}>{r.periodoTexto}</div>
        <div style={{ fontSize: 16, opacity: 0.65, marginTop: 28 }}>Dados do Escalone em {ctx.geradoEm.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</div>
      </div>
    </div>
  ));
  return S;
}

function slidesSlaMensal(
  sl: (id: string, nome: string, titulo: string, sub: string | undefined, corpo: () => React.ReactNode) => void,
  r: ResultadoApres,
) {
  const VERDE_T = '#15803d';
  const N = r.slaJanelas.length;
  const idx = N > 4 ? [N - 3, N - 2, N - 1] : Array.from({ length: N }, (_, i) => i);
  const comMedia = N > 4 || !!r.slaMesParcial;
  const cols = idx.map(i => r.slaJanelas[i]).concat(comMedia ? ['Média do período'] : []);
  const ok = (c: CelulaSla) => c.n >= 5 && c.medioH !== null;
  // tendência do 1º para o último mês mostrado: tempo médio (menor = melhor); % no SLA vai junto em pp
  const tend = (a: CelulaSla, b: CelulaSla) => {
    if (!ok(a) || !ok(b)) return null;
    const v = (b.medioH! - a.medioH!) / Math.max(a.medioH!, 0.01);
    const pp = a.pctSla !== null && b.pctSla !== null ? b.pctSla - a.pctSla : null;
    return { v, pp, neutro: Math.abs(v) < 0.1 };
  };
  const primeiro = idx[0], ultimo = idx[idx.length - 1];
  const linhas = r.slaMensal.map(l => ({ l, t: tend(l.meses[primeiro], l.meses[ultimo]) }));
  // título = a etapa com a maior variação em horas, dita em números
  const delta = (x: typeof linhas[number]) => Math.abs((x.l.meses[ultimo].medioH ?? 0) - (x.l.meses[primeiro].medioH ?? 0));
  const maior = linhas.filter(x => x.t && !x.t.neutro).sort((a, b) => delta(b) - delta(a))[0];
  const titulo = maior
    ? `${corta(maior.l.nome, 34)}: ${horasTxt(maior.l.meses[primeiro].medioH)} → ${horasTxt(maior.l.meses[ultimo].medioH)}`
    : 'Evolução do SLA por etapa';
  const tipoJ = r.slaCiclos ? 'ciclos de 30 dias' : 'mês a mês';
  const sub = `Evolução do SLA por etapa — ${tipoJ} · tempo médio, mediana, % no SLA e passagens · "—" = menos de 5${r.slaMesParcial ? ` · ${r.slaMesParcial} só na média` : ''}`;
  const POR = 9;
  const partes: typeof linhas[] = [];
  for (let i = 0; i < linhas.length; i += POR) partes.push(linhas.slice(i, i + POR));
  const grade = `250px repeat(${cols.length}, 1fr) 150px`;
  const celula = (c: CelulaSla, h: number) => !ok(c)
    ? <span style={{ color: TINTA3, fontSize: 20 }}>—</span>
    : (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
        <span style={{ fontSize: Math.min(22, h * 0.46), fontWeight: 800 }}>{horasTxt(c.medioH)}</span>
        <span style={{ fontSize: 12.5, color: TINTA3, whiteSpace: 'nowrap' }}>med {horasTxt(c.medianoH)}{c.pctSla !== null ? ` · ${Math.round(c.pctSla)}% no SLA` : ''} · {c.n}</span>
      </span>
    );
  const seta = (t: ReturnType<typeof tend>) => {
    if (!t) return <span style={{ color: TINTA3, fontSize: 15 }}>poucas passagens</span>;
    const cor = t.neutro ? TINTA3 : t.v < 0 ? VERDE_T : VERMELHO;
    return (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1, color: cor }}>
        <span style={{ fontSize: 22, fontWeight: 800 }}>{t.neutro ? '→' : t.v < 0 ? '↓' : '↑'} {t.v > 0 ? '+' : ''}{Math.round(t.v * 100)}%</span>
        {t.pp !== null && <span style={{ fontSize: 12.5 }}>SLA {t.pp >= 0 ? '+' : ''}{Math.round(t.pp)} pp</span>}
      </span>
    );
  };
  const fm = r.fluxoMensal;
  const fa = fm[primeiro], fb = fm[ultimo];
  const tc = fa.cicloMedioDias !== null && fb.cicloMedioDias !== null && fa.concluidos >= 5 && fb.concluidos >= 5
    ? (() => {
      const v = (fb.cicloMedioDias! - fa.cicloMedioDias!) / Math.max(fa.cicloMedioDias!, 0.01);
      return { v, pp: fa.pctNoPrazo !== null && fb.pctNoPrazo !== null ? fb.pctNoPrazo - fa.pctNoPrazo : null, neutro: Math.abs(v) < 0.1 };
    })()
    : null;
  const fluxoCel = (x: { concluidos: number; cicloMedioDias: number | null; pctNoPrazo: number | null }) => x.concluidos < 5
    ? <span style={{ color: '#94a3b8', fontSize: 20 }}>—</span>
    : (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
        <span style={{ fontSize: 22, fontWeight: 800 }}>{dias(x.cicloMedioDias)}</span>
        <span style={{ fontSize: 12.5, opacity: 0.8 }}>{x.pctNoPrazo !== null ? `${Math.round(x.pctNoPrazo)}% no prazo · ` : ''}{x.concluidos} concl.</span>
      </span>
    );
  partes.forEach((parte, pi) => sl(`slamensal${pi}`, partes.length > 1 ? `Evolução do SLA (${pi + 1}/${partes.length})` : 'Evolução do SLA',
    titulo + (partes.length > 1 ? ` (${pi + 1}/${partes.length})` : ''), sub, () => {
      const h = Math.min(44, Math.floor(360 / parte.length));
      return (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: grade, gap: 12, fontSize: 15, color: TINTA3, fontWeight: 600, paddingBottom: 8, borderBottom: '2px solid #e2e8f0' }}>
            <span style={{ textAlign: 'right' }}>Etapa</span>
            {cols.map(c => <span key={c} style={{ textAlign: 'right' }}>{c}</span>)}
            <span style={{ textAlign: 'right' }}>Tendência</span>
          </div>
          {parte.map(({ l, t }) => (
            <div key={l.id} style={{ display: 'grid', gridTemplateColumns: grade, gap: 12, alignItems: 'center', height: h, borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ textAlign: 'right', fontSize: Math.min(17, h * 0.4), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {l.nome}{l.slaH ? <span style={{ color: TINTA3, fontSize: 13 }}> · SLA {l.slaH} h</span> : null}
              </span>
              {idx.map(i => <span key={i} style={{ textAlign: 'right' }}>{celula(l.meses[i], h)}</span>)}
              {comMedia && <span style={{ textAlign: 'right' }}>{celula(l.total, h)}</span>}
              <span style={{ textAlign: 'right' }}>{seta(t)}</span>
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: grade, gap: 12, alignItems: 'center', marginTop: 14, padding: '12px 0', background: '#0f172a', color: '#fff', borderRadius: 14 }}>
            <span style={{ textAlign: 'right', fontSize: 17, fontWeight: 700 }}>Fluxo inteiro<br /><span style={{ fontSize: 12.5, fontWeight: 400, opacity: 0.75 }}>ciclo médio dos concluídos</span></span>
            {idx.map(i => <span key={i} style={{ textAlign: 'right' }}>{fluxoCel(fm[i])}</span>)}
            {comMedia && <span style={{ textAlign: 'right' }}>{fluxoCel(r.fluxoTotal)}</span>}
            <span style={{ textAlign: 'right', paddingRight: 14 }}>
              {!tc ? <span style={{ fontSize: 15, opacity: 0.7 }}>poucos concluídos</span> : (
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1, color: tc.neutro ? '#cbd5e1' : tc.v < 0 ? '#4ade80' : '#f87171' }}>
                  <span style={{ fontSize: 22, fontWeight: 800 }}>{tc.neutro ? '→' : tc.v < 0 ? '↓' : '↑'} {tc.v > 0 ? '+' : ''}{Math.round(tc.v * 100)}%</span>
                  {tc.pp !== null && <span style={{ fontSize: 12.5 }}>no prazo {tc.pp >= 0 ? '+' : ''}{Math.round(tc.pp)} pp</span>}
                </span>
              )}
            </span>
          </div>
        </div>
      );
    }));
}

function slideDimensao(
  sl: (id: string, nome: string, titulo: string, sub: string | undefined, corpo: () => React.ReactNode) => void,
  dim: Dimensao, todas: LinhaDim[], r: ResultadoApres, temValor: boolean,
) {
  const informadas = todas.filter(l => l.valor !== '(não informado)');
  const naoInf = todas.find(l => l.valor === '(não informado)');
  const porValor = temValor && informadas.some(l => l.valorTotal > 0);
  const ordenadas = [...informadas].sort((a, b) => porValor ? b.valorTotal - a.valorTotal || b.qtd - a.qtd : b.qtd - a.qtd);
  const N = 10;
  const top = ordenadas.slice(0, N);
  const resto = ordenadas.slice(N);
  const totalValor = todas.reduce((s, l) => s + l.valorTotal, 0);
  const titulo = dim.papel === 'categoria' ? `Por ${dim.nome.toLowerCase()}` : NOME_PAPEL[dim.papel];
  const sub = `${informadas.length} ${informadas.length === 1 ? 'valor' : 'valores'} no campo "${dim.nome}" · entradas do período`
    + (porValor ? ' · ordenado por valor' : ' · ordenado por quantidade')
    + (naoInf ? ` · ${int(naoInf.qtd)} sem preenchimento` : '');
  sl(`dim_${dim.id}`, titulo, titulo, sub, () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 36 }}>
      <Barras altura={470} larguraRotulo={230}
        linhas={top.map(l => ({ rotulo: l.valor, valor: porValor ? l.valorTotal : l.qtd, txt: porValor ? moedaCurta(l.valorTotal) : int(l.qtd) }))} />
      <Tabela fonte={top.length > 8 ? 14 : 15}
        cab={porValor ? [dim.nome.length > 16 ? 'Valor' : dim.nome, 'Cards', 'Concl.', 'Aberto', 'R$', '% R$'] : [dim.nome.length > 16 ? 'Valor' : dim.nome, 'Cards', '%', 'Concl.', 'Aberto', 'Ciclo']}
        alinhar={['l', 'r', 'r', 'r', 'r', 'r']}
        linhas={[
          ...top.map(l => porValor
            ? [corta(l.valor, 24), int(l.qtd), int(l.concluidos), int(l.abertos), moedaCurta(l.valorTotal), pct(l.valorTotal, totalValor)]
            : [corta(l.valor, 26), int(l.qtd), pct(l.qtd, r.entradas), int(l.concluidos), int(l.abertos), dias(l.cicloMedioDias)]),
          ...(resto.length ? [porValor
            ? [`Demais (${resto.length})`, int(resto.reduce((s, l) => s + l.qtd, 0)), int(resto.reduce((s, l) => s + l.concluidos, 0)), int(resto.reduce((s, l) => s + l.abertos, 0)), moedaCurta(resto.reduce((s, l) => s + l.valorTotal, 0)), pct(resto.reduce((s, l) => s + l.valorTotal, 0), totalValor)]
            : [`Demais (${resto.length})`, int(resto.reduce((s, l) => s + l.qtd, 0)), pct(resto.reduce((s, l) => s + l.qtd, 0), r.entradas), int(resto.reduce((s, l) => s + l.concluidos, 0)), int(resto.reduce((s, l) => s + l.abertos, 0)), '']] : []),
        ]} />
    </div>
  ));
}

// ---------- tela ----------

const ApresentacaoFluxo: React.FC<Props> = ({ flow, cards: cardsApp, colaboradores }) => {
  const sess = useMemo(sessao, []);
  const chavePref = `escalone_apres_${flow.id}`;
  const pref = useMemo(() => lerPref(chavePref) || {}, [chavePref]);

  const [cards, setCards] = useState<CardDef[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [periodo, setPeriodo] = useState<PeriodoId>(pref.periodo || 'ano');
  const [de, setDe] = useState(''); const [ate, setAte] = useState('');
  const [filtros, setFiltros] = useState<Record<string, string[]>>({});
  const [campoValor, setCampoValor] = useState<string>(pref.campoValor ?? 'auto');
  const [tiposMan, setTiposMan] = useState<Record<string, TipoEtapa>>(pref.tipos || {});
  const [ocultos, setOcultos] = useState<string[]>(pref.ocultos || []);
  const [ligados, setLigados] = useState<string[]>(pref.ligados || []);
  const [ajustes, setAjustes] = useState(false);
  const [apresentando, setApresentando] = useState(false);
  const [atual, setAtual] = useState(0);
  const [imprimindo, setImprimindo] = useState(false);
  const [geradoEm, setGeradoEm] = useState(() => new Date());
  const palco = useRef<HTMLDivElement>(null);

  useEffect(() => { gravarPref(chavePref, { periodo, campoValor, tipos: tiposMan, ocultos, ligados }); }, [chavePref, periodo, campoValor, tiposMan, ocultos, ligados]);

  // Busca própria do fluxo: o resumo com os valores que só ficaram no histórico (preencherSnapshot)
  useEffect(() => {
    let vivo = true;
    setCarregando(true); setCards(null);
    const url = `/api/cards/resumo?empresaId=${encodeURIComponent(sess.empresaId)}&userEmail=${encodeURIComponent(sess.email)}&flowId=${encodeURIComponent(flow.id)}&preencherSnapshot=true`;
    fetch(url).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(d => {
      if (!vivo) return;
      setCards(Array.isArray(d) && d.length ? d : (cardsApp as any[]).filter(c => String((c as any).flowId) === String(flow.id)));
      setGeradoEm(new Date());
    }).catch(() => {
      if (vivo) setCards((cardsApp as any[]).filter(c => String((c as any).flowId) === String(flow.id)));
    }).finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.id]);

  const fluxo = flow as unknown as FluxoDef;
  const nomeColab = useMemo(() => new Map(colaboradores.map(c => [String(c.id), c.nome])), [colaboradores]);
  const lista = cards || [];
  const tiposAuto = useMemo(() => tiposAutomaticos(fluxo, lista), [fluxo, lista]);
  const tipos = useMemo(() => ({ ...tiposAuto, ...tiposMan }), [tiposAuto, tiposMan]);
  const dims = useMemo(() => detectarDimensoes(fluxo, lista, nomeColab), [fluxo, lista, nomeColab]);
  const dimsFiltro = dims.filter(d => d.papel === 'filial' || d.papel === 'centro');
  const opcoesFiltro = useMemo(() => {
    const m: Record<string, string[]> = {};
    dimsFiltro.forEach(d => {
      const s = new Set<string>(); lista.forEach(c => d.valores(c).forEach(v => s.add(v)));
      m[d.id] = Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
    });
    return m;
  }, [dimsFiltro, lista]);
  const { todos: valoresTodos, auto: valoresAuto } = useMemo(() => camposValor(fluxo), [fluxo]);

  const resultado = useMemo(() => {
    if (!cards) return null;
    const { inicio, fim } = intervaloDe(periodo, new Date(), de, ate);
    return calcular(fluxo, cards, { inicio, fim, tipos, campoValor, filtros, periodoId: periodo }, dims, calcularHorasSLA);
  }, [cards, periodo, de, ate, tipos, campoValor, filtros, dims, fluxo]);

  const filtrosTxt = Object.entries(filtros).filter(([, v]) => v.length).map(([id, v]) => `${dims.find(d => d.id === id)?.nome}: ${v.join(', ')}`).join(' · ');
  const slidesTodos = useMemo(() => resultado
    ? montarSlides(resultado, { empresa: sess.empresaNome, filtrosTxt, geradoEm, temValor: !!resultado.valorNome && resultado.valorEntradas > 0 })
    : [], [resultado, sess.empresaNome, filtrosTxt, geradoEm]);
  const ligado = (id: string) => DESLIGADOS_PADRAO.includes(id) ? ligados.includes(id) : !ocultos.includes(id);
  const slides = slidesTodos.filter(s => s.id === 'capa' || ligado(s.id));

  // ----- apresentar -----
  const ir = useCallback((d: number) => setAtual(a => Math.max(0, Math.min(slides.length - 1, a + d))), [slides.length]);
  const sair = useCallback(() => { setApresentando(false); if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); }, []);
  useEffect(() => {
    if (!apresentando) return;
    const k = (e: KeyboardEvent) => {
      if (['ArrowRight', 'PageDown', ' ', 'Enter', 'ArrowDown'].includes(e.key)) { e.preventDefault(); ir(1); }
      else if (['ArrowLeft', 'PageUp', 'Backspace', 'ArrowUp'].includes(e.key)) { e.preventDefault(); ir(-1); }
      else if (e.key === 'Home') setAtual(0);
      else if (e.key === 'End') setAtual(slides.length - 1);
      else if (e.key === 'Escape') sair();
    };
    const fs = () => { if (!document.fullscreenElement) setApresentando(false); };
    document.addEventListener('keydown', k);
    document.addEventListener('fullscreenchange', fs);
    return () => { document.removeEventListener('keydown', k); document.removeEventListener('fullscreenchange', fs); };
  }, [apresentando, ir, sair, slides.length]);
  const [tela, setTela] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const r = () => setTela({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', r); return () => window.removeEventListener('resize', r);
  }, []);
  const comecar = async (de0 = 0) => {
    setAtual(de0); setApresentando(true);
    setTimeout(() => { palco.current?.requestFullscreen?.().catch(() => { /* sem tela cheia: fica na janela */ }); }, 30);
  };

  // ----- PDF -----
  useEffect(() => {
    if (!imprimindo) return;
    const fim = () => setImprimindo(false);
    window.addEventListener('afterprint', fim);
    const t = setTimeout(() => { try { window.print(); } catch { /* fica a prévia */ } }, 800);
    return () => { clearTimeout(t); window.removeEventListener('afterprint', fim); };
  }, [imprimindo]);

  const ciclarTipo = (id: string) => setTiposMan(t => {
    const ordem: TipoEtapa[] = ['andamento', 'concluida', 'negativa'];
    const atualT = tipos[id] || 'andamento';
    const prox = ordem[(ordem.indexOf(atualT) + 1) % 3];
    const n = { ...t, [id]: prox };
    if (prox === tiposAuto[id]) delete n[id];
    return n;
  });

  if (carregando) return (
    <div className="bg-white rounded-xl border border-gray-200/50 p-12 flex items-center justify-center gap-3 text-sm text-gray-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Lendo os cards do fluxo…
    </div>
  );
  if (!resultado) return null;

  const chip = (ativo: boolean) => clsx('px-3 py-1.5 rounded-md text-xs font-medium transition-colors', ativo ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900');
  const corTipo: Record<TipoEtapa, string> = { andamento: 'bg-gray-100 text-gray-700 border-gray-200', concluida: 'bg-emerald-50 text-emerald-700 border-emerald-200', negativa: 'bg-rose-50 text-rose-700 border-rose-200' };
  const nomeTipo: Record<TipoEtapa, string> = { andamento: 'andamento', concluida: 'conclusão', negativa: 'reprovação' };
  const escPalco = Math.min(tela.w / W, tela.h / H);

  return (
    <div className="space-y-4">
      {/* controles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center"><Presentation className="h-4 w-4 text-indigo-600" /></div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Apresentação · {resultado.nomeFluxo}</h3>
              <p className="text-xs text-gray-500">{slides.length} slides montados com os cards do fluxo · {resultado.periodoTexto}</p>
            </div>
          </div>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {PERIODOS.map(p => <button key={p.id} onClick={() => setPeriodo(p.id)} className={chip(periodo === p.id)}>{p.label}</button>)}
          </div>
          {periodo === 'custom' && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <input type="date" value={de} onChange={e => setDe(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-md text-xs" />
              até <input type="date" value={ate} onChange={e => setAte(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-md text-xs" />
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={() => setAjustes(a => !a)} className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border', ajustes ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')}>
              <Settings2 className="w-4 h-4" /> Ajustar
            </button>
            <button onClick={() => { setGeradoEm(new Date()); setImprimindo(true); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
              <FileDown className="w-4 h-4" /> PDF
            </button>
            <button onClick={() => comecar(0)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm">
              <Presentation className="w-4 h-4" /> Apresentar
            </button>
          </div>
        </div>

        {dimsFiltro.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {dimsFiltro.map(d => (
              <label key={d.id} className="flex items-center gap-2 text-gray-600">
                <span className="font-medium">{d.nome}</span>
                <select value={filtros[d.id]?.[0] || ''} onChange={e => setFiltros(f => ({ ...f, [d.id]: e.target.value ? [e.target.value] : [] }))}
                  className="px-2 py-1.5 border border-gray-300 rounded-md text-xs bg-white max-w-[220px]">
                  <option value="">Todas</option>
                  {(opcoesFiltro[d.id] || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            ))}
            {valoresTodos.length > 0 && (
              <label className="flex items-center gap-2 text-gray-600">
                <span className="font-medium">Valor</span>
                <select value={campoValor} onChange={e => setCampoValor(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-md text-xs bg-white max-w-[260px]">
                  {valoresAuto.length > 0 && <option value="auto">Automático ({valoresAuto.map(c => nomeLimpo(c.nome)).join(' → ')})</option>}
                  {valoresTodos.map(c => <option key={c.id} value={c.id}>{nomeLimpo(c.nome)}</option>)}
                  <option value="">Sem valor</option>
                </select>
              </label>
            )}
          </div>
        )}

        {ajustes && (
          <div className="grid gap-4 lg:grid-cols-2 pt-2 border-t border-gray-100">
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1.5">Papel de cada etapa <span className="font-normal text-gray-400">(clique para trocar: andamento → conclusão → reprovação)</span></div>
              <div className="flex flex-wrap gap-1.5">
                {flow.etapas.map(e => {
                  const t = tipos[e.id] || 'andamento';
                  return (
                    <button key={e.id} onClick={() => ciclarTipo(e.id)} className={clsx('px-2 py-1 rounded-md border text-xs', corTipo[t])} title={tiposMan[e.id] ? 'ajustado por você' : 'automático'}>
                      {e.nome} · <b>{nomeTipo[t]}</b>{tiposMan[e.id] ? ' *' : ''}
                    </button>
                  );
                })}
              </div>
              {Object.keys(tiposMan).length > 0 && <button onClick={() => setTiposMan({})} className="mt-1.5 text-xs text-gray-500 underline">voltar ao automático</button>}
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1.5">Slides</div>
              <div className="flex flex-wrap gap-1.5">
                {slidesTodos.filter(s => s.id !== 'capa').map(s => {
                  const on = ligado(s.id);
                  const alternar = () => DESLIGADOS_PADRAO.includes(s.id)
                    ? setLigados(o => on ? o.filter(x => x !== s.id) : [...o, s.id])
                    : setOcultos(o => on ? [...o, s.id] : o.filter(x => x !== s.id));
                  return (
                    <button key={s.id} onClick={alternar}
                      className={clsx('flex items-center gap-1 px-2 py-1 rounded-md border text-xs', on ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-200 text-gray-400 line-through')}>
                      {on && <Check className="w-3 h-3" />}{s.nome}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* prévia */}
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {slides.map((s, i) => (
          <button key={s.id} onClick={() => comecar(i)} className="group text-left rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-indigo-300 transition">
            <Moldura>{s.render(i + 1, slides.length)}</Moldura>
            <div className="px-3 py-2 text-xs text-gray-600 border-t border-gray-100 flex justify-between"><span>{i + 1}. {s.nome}</span><span className="text-indigo-600 opacity-0 group-hover:opacity-100">apresentar daqui</span></div>
          </button>
        ))}
      </div>

      {/* tela cheia */}
      {apresentando && createPortal(
        <div ref={palco} className="apres-palco" style={{ position: 'fixed', inset: 0, zIndex: 2147483000, background: '#05070d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { const x = e.clientX / window.innerWidth; ir(x < 0.3 ? -1 : 1); }}>
          <div style={{ width: W * escPalco, height: H * escPalco, position: 'relative' }}>
            <div style={{ width: W, height: H, transform: `scale(${escPalco})`, transformOrigin: 'top left', position: 'absolute' }}>{slides[atual]?.render(atual + 1, slides.length)}</div>
          </div>
          <div className="apres-ctrl" onClick={e => e.stopPropagation()} style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(15,23,42,.85)', color: '#fff', borderRadius: 999, padding: '6px 10px', fontSize: 13 }}>
            <button onClick={() => ir(-1)} className="p-1 hover:bg-white/10 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
            <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 54, textAlign: 'center' }}>{atual + 1} / {slides.length}</span>
            <button onClick={() => ir(1)} className="p-1 hover:bg-white/10 rounded-full"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={sair} className="p-1 hover:bg-white/10 rounded-full ml-1" title="Sair (Esc)"><X className="w-4 h-4" /></button>
          </div>
          <style>{`.apres-palco .apres-ctrl{opacity:.0;transition:opacity .2s}.apres-palco:hover .apres-ctrl{opacity:1}`}</style>
        </div>, document.body)}

      {/* PDF: todas as páginas 16:9 num portal; fora dele tudo some na impressão */}
      {imprimindo && createPortal(
        <div className="apres-print">
          <style>{`
            @page { size: ${W}px ${H}px; margin: 0 }
            @media screen { .apres-print{position:fixed;inset:0;z-index:2147483000;overflow:auto;background:#e5e7eb;padding:16px}
              .apres-print .apres-pag{margin:0 auto 16px;box-shadow:0 1px 4px rgba(0,0,0,.2)} }
            @media print { html,body{height:auto!important;overflow:visible!important;background:#fff!important;margin:0!important}
              body>*:not(.apres-print){display:none!important}
              .apres-print .apres-barra{display:none!important}
              .apres-print .apres-pag{break-after:page;page-break-after:always}
              .apres-print .apres-pag:last-child{break-after:auto;page-break-after:auto} }
            .apres-print,.apres-print *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
            .apres-print .apres-pag{width:${W}px;height:${H}px;overflow:hidden}
          `}</style>
          <div className="apres-barra" style={{ display: 'flex', gap: 8, maxWidth: W, margin: '0 auto 12px', alignItems: 'center', fontSize: 13 }}>
            <span className="text-gray-600">{slides.length} páginas deitadas (16:9). Na janela de impressão, escolha "Salvar como PDF".</span>
            <button onClick={() => window.print()} className="ml-auto rounded-lg bg-gray-900 px-3 py-1.5 font-medium text-white">Imprimir / salvar PDF</button>
            <button onClick={() => setImprimindo(false)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700">Fechar</button>
          </div>
          {slides.map((s, i) => <div key={s.id} className="apres-pag">{s.render(i + 1, slides.length)}</div>)}
        </div>, document.body)}
    </div>
  );
};

export default ApresentacaoFluxo;
